// React and Utilities
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { t } from 'i18next';

// PrimeReact Components
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Card } from 'primereact/card';

// API and Context
import { useToast } from '../../../../../../../src/app/context/ToastProvider';
import { apiServices } from '../../../../../../../src/app/services/apiServices';

// Models and Types
import {
  GraphsProps,
  Alarms,
} from '../../../../../../../src/app/models/graphsModels';
import { DeviceModelType } from '../../../../../../../src/app/models/devicesModels';

// Custom Components
import AreaChartCustom from '../../../../../../../src/app/components/area-chart/area-chart';
import ConfigPatient from '../../../../../../../src/app/components/config_patient/dashboard';
import MultiLineChart from '../../../../../../../src/app/components/multi-line-chart/multi-line-chart';
import TestCard from '../test-card/testCard';
import DeviceVersionCard from '../device-version-card/deviceVersionCard';
import BrushGraphs from '../../../../../../../src/app/components/brush-graphs/brush-graphs';
import { ProgressBarComponent } from '../../../../../../../src/app/components/progress-bar/progress-bar';

// Constants
import {
  stateLabelsAtlas,
  stateLabelsExplorer,
  stateLabelsExplorerClinic,
  securityStatusLabels,
  globalPositionLabels,
} from '../../../../../../../src/app/constants/constants';
import { splitZipName } from '@marsinet/commons';

const MAX_SELECTION_RANGE = 2200;

export function Graphs(props: Readonly<GraphsProps>) {
  const {
    ingestionId,
    setShowGraphs,
    zip_data,
    isTest,
    serialNumber,
    modelDevices,
  } = props;

  const { showToast: show } = useToast();
  const [isMobile, setIsMobile] = useState(false);
  const [showScrollTopButton, setShowScrollTopButton] = useState(false);

  const [selectedAlarm, setSelectedAlarm] = useState(null);

  const [fullData, setFullData] = useState({
    sessionData: [],
    motorsAng: [],
    torqueData: [],
    temperatureData: [],
    motorVoltageData: [],
    configPatient: {},
    energyMonitor: [],
    globalPosition: [],
    elevSens: [],
    elevCtrl: [],
    motorsInt: [],
    state: [],
    loadCell: [],
    sitAngle: [],
    sysStatus: [],
    envTemp: [],
    alarms: [],
    indexes: [],
    capacityData: [],
    voltageData: [],
    tempData: [],
    tempEpos: [],
    imuData: [],
    assistedTorque: [],
    refAndRealTorque: [],
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [jsonHeaderData, setJsonHeaderData] = useState<any>({});

  const [displayedData, setDisplayedData] = useState(fullData);

  const [startIndex, setStartIndex] = useState(0);
  const [endIndex, setEndIndex] = useState(0);
  const [alarms, setAlarms] = useState<Alarms[]>([]);
  const [indexes, setIndexes] = useState([]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [configPatient, setConfigPatient] = useState<any>({});
  const [loading, setLoading] = useState(true);
  // Estado para determinar si el dispositivo es Atlas

  const [isAtlas, setIsAtlas] = useState(false);
  const isClinic =
    modelDevices ===
    (DeviceModelType.EXPLORER_CLINICO as unknown as typeof modelDevices);

  const [graphsVisibility, setGraphsVisibility] = useState({
    generalState: true,
    battery: false,
    motorsRef: true,
    motorsTorque: false,
    motorsTemp: false,
    motorsCurrent: false,
    motorsInt: false,
    energy_monitor: false,
    global_position: false,
    elev_sens: false,
    elev_ctrl: false,
    sit_angle: false,
    load_cell: false,
    state: true,
    battery_voltage: false,
    battery_temp: false,
    sys_status: false,
    env_temp: false,
    tempEpos: false,
    imuData: false,
    assistedTorque: false,
    refAndRealTorque: false,
  });

  const highlightedPoints = useMemo(
    () =>
      alarms.map((alarm) => ({
        timestamp: alarm.timestamp,
        info: {
          eventType: alarm.event_type,
          eventId: alarm.event_id,
          params: alarm.params,
          value: alarm.value,
        },
      })),
    [alarms]
  );

  const checkboxGroups = useMemo(() => {
    const excludedFieldsForAtlas = new Set([
      'global_position',
      'battery_voltage',
      'battery_temp',
      'energy_monitor',
      'tempEpos',
      'elev_sens',
      'elev_ctrl',
      'sit_angle',
      'load_cell',
      'env_temp',
      'assistedTorque',
      'refAndRealTorque',
      'imuData',
    ]);

    const rawGroups = [
      {
        title: t('pages.maintenance.ingestion.generalInformation'),
        items: [
          {
            name: 'generalState',
            label: t('pages.maintenance.ingestion.generalState'),
          },
          {
            name: 'sys_status',
            label: t('pages.maintenance.ingestion.sys_status'),
          },
          { name: 'state', label: t('pages.maintenance.ingestion.state') },
          {
            name: 'global_position',
            label: t('pages.maintenance.ingestion.global_position'),
          },
        ],
      },
      {
        title: t('pages.maintenance.ingestion.batery'),
        items: [
          {
            name: 'battery',
            label: t('pages.maintenance.ingestion.percentage'),
          },
          {
            name: 'battery_voltage',
            label: t('pages.maintenance.ingestion.voltage'),
          },
          {
            name: 'energy_monitor',
            label: t('pages.maintenance.ingestion.energy_monitor'),
          },
          {
            name: 'battery_temp',
            label: t('pages.maintenance.ingestion.temperature'),
          },
        ],
      },
      {
        title: t('pages.maintenance.ingestion.exo'),
        items: [
          {
            name: 'motorsRef',
            label: t('pages.maintenance.ingestion.motorsRef'),
          },
          {
            name: 'motorsCurrent',
            label: t('pages.maintenance.ingestion.motorsCurrent'),
          },
          {
            name: 'motorsTemp',
            label: t('pages.maintenance.ingestion.motorsTemp'),
          },
          {
            name: 'motorsTorque',
            label: t('pages.maintenance.ingestion.motorsTorque'),
          },
          {
            name: 'motorsInt',
            label: t('pages.maintenance.ingestion.motorsInt'),
          },
          {
            name: 'tempEpos',
            label: t('pages.maintenance.ingestion.tempEpos'),
          },
          {
            name: 'assistedTorque',
            label: t('pages.maintenance.ingestion.assistedTorque'),
          },
          {
            name: 'refAndRealTorque',
            label: t('pages.maintenance.ingestion.refAndRealTorque'),
          },
        ],
      },
      {
        title: t('pages.maintenance.ingestion.walker_info'),
        items: [
          {
            name: 'elev_sens',
            label: t('pages.maintenance.ingestion.elev_sens'),
          },
          {
            name: 'elev_ctrl',
            label: t('pages.maintenance.ingestion.elev_control'),
          },
          {
            name: 'sit_angle',
            label: t('pages.maintenance.ingestion.sit_angle'),
          },
          {
            name: 'load_cell',
            label: t('pages.maintenance.ingestion.loadCell'),
          },
          {
            name: 'env_temp',
            label: t('pages.maintenance.ingestion.env_temp'),
          },
          {
            name: 'imuData',
            label: t('pages.maintenance.ingestion.imuData'),
          },
        ],
      },
    ];

    return rawGroups
      .map((group) => ({
        ...group,
        items: isAtlas
          ? group.items.filter((item) => !excludedFieldsForAtlas.has(item.name))
          : group.items,
      }))
      .filter((group) => group.items.length > 0);
  }, [isAtlas, t]);

  const stateLabels = useMemo(() => {
    if (isAtlas) return stateLabelsAtlas;
    if (isClinic) return stateLabelsExplorerClinic;
    return stateLabelsExplorer;
  }, [isAtlas, isClinic]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let response;
        try {
          response = await apiServices.ingestionApi.ingestionControllerFindOne(
            ingestionId,
            'maintenance'
          );
        } catch (findOneError) {
          if (findOneError?.response?.status === 413) {
            show(
              'error',
              t('messages.error'),
              t('messages.ingestion.payload_too_large')
            );
            setLoading(false)
            return;
          }
          show('error', t('messages.error'), t('messages.error_ingestion'));
          setLoading(false);
          return;
        }

        // Extract both metadata and CSV data from response
        const jsonData = response.data?.metadata;
        const csvData = response.data?.csvData;

        if (!jsonData) {
          console.error('Metadata not found in response');
          show(
            'error',
            t('messages.error'),
            t('messages.error_ingestion') + ' (header)'
          );
          setLoading(false);
          return;
        }

        if (!csvData) {
          console.error('CSV data not found in response');
          show(
            'error',
            t('messages.error'),
            t('messages.error_ingestion') + ' (csv)'
          );
          setLoading(false);
          return;
        }

        setJsonHeaderData(jsonData);

        if (csvData) {
          const {
            sessionData,
            motorsAng,
            torqueData,
            temperatureData,
            motorVoltageData,
            config_patient,
            energyMonitor,
            globalPosition,
            elevSens,
            elevCtrl,
            motorsInt,
            state,
            loadCellData,
            sitAngle,
            sys_system,
            envTemp,
            batteryData,
            alarmsArray,
            indexes,
            tempEpos,
            isAtlas,
            imuData,
            assistedTorque,
            refAndRealTorque,
          } = csvData;

          setIsAtlas(isAtlas);
          setAlarms(alarmsArray);
          setIndexes(indexes);
          setConfigPatient(config_patient);
          setEndIndex(Math.floor(sessionData.length * 0.05));

          const capacityData = batteryData.map(({ timestamp, capacity }) => ({
            timestamp,
            capacity,
          }));
          const voltageData = batteryData.map(({ timestamp, voltage }) => ({
            timestamp,
            voltage,
          }));
          const tempData = batteryData.map(({ timestamp, temp }) => ({
            timestamp,
            temperature: temp,
          }));

          const data = {
            sessionData,
            motorsAng,
            torqueData,
            temperatureData,
            motorVoltageData,
            configPatient: config_patient,
            energyMonitor,
            globalPosition,
            elevSens,
            elevCtrl,
            motorsInt,
            state,
            loadCell: loadCellData,
            sitAngle,
            sysStatus: sys_system,
            envTemp,
            alarms: alarmsArray,
            indexes,
            capacityData,
            voltageData,
            tempData,
            tempEpos,
            imuData,
            assistedTorque,
            refAndRealTorque,
          };

          setFullData(data);
          setDisplayedData(data);
        }
        setLoading(false);
      } catch (_error) {
        console.error('Error general en fetchData:', _error);
        show('error', t('messages.error'), t('messages.error_ingestion'), 8000);
        setLoading(false);
      }
    };

    fetchData();
  }, [ingestionId]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768); // Pantallas menores a 768px
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const halfHeight = document.documentElement.scrollHeight / 2;
      setShowScrollTopButton(window.scrollY > halfHeight);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle alarm selection and update displayed data accordingly
  useEffect(() => {
    if (selectedAlarm) {
      const alarmIndex = alarms.findIndex(
        (alarm) =>
          alarm.event_id === selectedAlarm.event_id &&
          alarm.timestamp === selectedAlarm.timestamp
      );

      const startIndexValue = indexes[alarmIndex + 1];
      const endIndexValue = indexes[alarmIndex + 2];

      setStartIndex(0);
      setEndIndex(Math.floor((endIndexValue - startIndexValue) * 0.5));

      setDisplayedData({
        ...fullData,
        sessionData: fullData.sessionData.slice(startIndexValue, endIndexValue),
        motorsAng: fullData.motorsAng.slice(startIndexValue, endIndexValue),
        torqueData: fullData.torqueData.slice(startIndexValue, endIndexValue),
        temperatureData: fullData.temperatureData.slice(
          startIndexValue,
          endIndexValue
        ),
        motorVoltageData: fullData.motorVoltageData.slice(
          startIndexValue,
          endIndexValue
        ),
        energyMonitor: fullData.energyMonitor.slice(
          startIndexValue,
          endIndexValue
        ),
        globalPosition: fullData.globalPosition.slice(
          startIndexValue,
          endIndexValue
        ),
        elevSens: fullData.elevSens.slice(startIndexValue, endIndexValue),
        elevCtrl: fullData.elevCtrl.slice(startIndexValue, endIndexValue),
        motorsInt: fullData.motorsInt.slice(startIndexValue, endIndexValue),
        state: fullData.state.slice(startIndexValue, endIndexValue),
        loadCell: fullData.loadCell.slice(startIndexValue, endIndexValue),
        sitAngle: fullData.sitAngle.slice(startIndexValue, endIndexValue),
        sysStatus: fullData.sysStatus.slice(startIndexValue, endIndexValue),
        envTemp: fullData.envTemp.slice(startIndexValue, endIndexValue),
        capacityData: fullData.capacityData.slice(
          startIndexValue,
          endIndexValue
        ),
        voltageData: fullData.voltageData.slice(startIndexValue, endIndexValue),
        tempData: fullData.tempData.slice(startIndexValue, endIndexValue),
        imuData: fullData.imuData.slice(startIndexValue, endIndexValue),
        assistedTorque: fullData.assistedTorque.slice(
          startIndexValue,
          endIndexValue
        ),
        refAndRealTorque: fullData.refAndRealTorque.slice(
          startIndexValue,
          endIndexValue
        ),
      });
    } else {
      setStartIndex(0);
      setEndIndex(Math.floor(fullData.sessionData.length * 0.05));
      setDisplayedData(fullData);
    }
  }, [selectedAlarm]);

  // Event handlers for UI interactions
  const handleCheckboxChange = (name) => (e) => {
    setGraphsVisibility((prevState) => ({ ...prevState, [name]: e.checked }));
  };

  const handleBrushChange = ({
    startIndex,
    endIndex,
  }: {
    startIndex: number;
    endIndex: number;
  }) => {
    const range = endIndex - startIndex;

    if (range > MAX_SELECTION_RANGE) {
      show(
        'warn',
        'Advertencia',
        t('pages.maintenance.ingestion.graphs.outrange')
      );
      setStartIndex(startIndex);
      setEndIndex(startIndex + MAX_SELECTION_RANGE);
    } else {
      setStartIndex(startIndex);
      setEndIndex(endIndex);
    }
  };

  const handleClearCheckbox = () => {
    setGraphsVisibility({
      generalState: false,
      battery: false,
      motorsRef: false,
      motorsTorque: false,
      motorsTemp: false,
      motorsCurrent: false,
      motorsInt: false,
      energy_monitor: false,
      global_position: false,
      elev_sens: false,
      elev_ctrl: false,
      sit_angle: false,
      load_cell: false,
      state: false,
      battery_voltage: false,
      battery_temp: false,
      sys_status: false,
      env_temp: false,
      tempEpos: false,
      imuData: false,
      assistedTorque: false,
      refAndRealTorque: false,
    });
  };

  const scrollToButton = () => {
    const clearButton = document.getElementById('clearButton');
    if (clearButton)
      clearButton.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleAlarmSelection = (alarm) => {
    setSelectedAlarm((prevSelectedAlarm) =>
      prevSelectedAlarm &&
        prevSelectedAlarm.event_id === alarm.event_id &&
        prevSelectedAlarm.timestamp === alarm.timestamp
        ? null
        : {
          event_id: alarm.event_id,
          timestamp: alarm.timestamp,
          date: alarm.date,
          event_type: alarm.event_type,
          value: alarm.value,
          params: alarm.params,
        }
    );
  };

  return (
    <div>
      {loading ? (
        <ProgressBarComponent />
      ) : (
        <div className="col-12 grid grid-nogutter">
          <div className="surface-card border-round-2xl p-4 max-w-screen-lg mx-auto relative">
            <div
              className="flex items-center justify-between flex-wrap"
              style={{ justifyContent: 'space-between' }}
            >
              {/* Botón Izquierdo */}
              <Button
                icon="pi pi-angle-left"
                rounded
                onClick={() => setShowGraphs(false)}
                severity="secondary"
                className="mr-4 sm:mb-0 mt-4"
                aria-label="Back"
              />

              {/* Título Centrado */}
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center">
                {t('pages.maintenance.ingestion.file') +
                  ' ' +
                  splitZipName(zip_data)}
              </h1>

              {/* Espacio vacío opcional */}
              <div
                className="hidden sm:block"
                style={{ width: '2.5rem' }}
              ></div>
            </div>
            {isMobile ? (
              <div className="text-center text-lg font-bold text-red-500 mt-4">
                {t('pages.reports.graphs.mobile')}
              </div>
            ) : (
              <>
                {!isTest ? (
                  <ConfigPatient config={configPatient} isAtlas={isAtlas} />
                ) : (
                  <TestCard
                    alarms={alarms}
                    selectedAlarm={selectedAlarm}
                    handleAlarmSelection={handleAlarmSelection}
                  />
                )}
                {/* Sección para versiones de la ingesta */}
                <DeviceVersionCard
                  jsonHeaderData={jsonHeaderData}
                  serialNumber={serialNumber}
                  t={t}
                />
                {/* Sección con los checkboxes */}
                <div
                  className="flex justify-content items-center gap-4 flex-wrap  align-center align-content"
                  id="clearButton"
                >
                  {checkboxGroups.map((group) => (
                    <Card
                      key={group.title}
                      className="transition-all duration-300 ease-in-out surface-card border-round-md shadow-3 p-2 m-2 rounded-md mx-auto"
                      style={{ width: '280px', textAlign: 'center' }}
                    >
                      <h3 className="text-base font-semibold text-center">
                        {group.title}
                      </h3>
                      <div className="ml-3 mt-2 flex-col gap-4 justify-center">
                        {group.items.map((item) => (
                          <div
                            key={item.name}
                            className="flex flex-col items-center gap-2 my-2 transition-all duration-300 rounded-lg justify-center"
                          >
                            <Checkbox
                              checked={graphsVisibility[item.name]}
                              onChange={handleCheckboxChange(item.name)}
                              className="transition-all duration-300"
                              disabled={loading}
                              style={{ width: '18px', height: '18px' }}
                            />
                            <label className="transition-all duration-300 hover:text-white/70 text-sm">
                              {item.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </Card>
                  ))}
                </div>
                <Button
                  onClick={handleClearCheckbox}
                  label={t('clear')}
                  className="flex flex-wrap justify-center items-center col-6 mx-auto mt-4"
                />
                {/*Bursh*/}
                <div
                  className="sticky"
                  style={{
                    top: '4rem',
                    zIndex: 4,
                    background: 'var(--surface-card)',
                  }}
                >
                  <BrushGraphs
                    data={displayedData.sessionData}
                    startIndex={startIndex}
                    endIndex={endIndex}
                    onBrushChange={handleBrushChange}
                    alarms={alarms}
                    selectedAlarm={selectedAlarm}
                  />
                </div>

                {/* Gráficas agrupadas por card */}
                <div className="w-full grid grid-cols-1 gap-4 mt-4">
                  {/* Gráficas del grupo "Información General" */}
                  {(graphsVisibility.generalState ||
                    graphsVisibility.state ||
                    graphsVisibility.global_position ||
                    graphsVisibility.sys_status) && (
                      <Card className="w-full p-4 shadow-xl rounded-lg surface-card border-round-xl shadow-3">
                        <div className="text-4xl font-bold mb-4 ">
                          {t('pages.maintenance.ingestion.generalInformation')}
                        </div>
                        {graphsVisibility.generalState && (
                          <Suspense fallback={<ProgressSpinner />}>
                            <AreaChartCustom
                              data={displayedData.sessionData.slice(
                                startIndex,
                                endIndex
                              )}
                              title={t(
                                'pages.maintenance.ingestion.generalState'
                              )}
                              setLoading={setLoading}
                              chartOptions={{
                                type: 'stepAfter',
                                yAxisDomain: [0, 1],
                                tickCount: 2,
                                tickFormatter: (value) =>
                                  value === 1 ? 'On' : 'Off',
                              }}
                              highlightedPoints={highlightedPoints}
                            />
                          </Suspense>
                        )}
                        {graphsVisibility.sys_status && (
                          <Suspense fallback={<ProgressSpinner />}>
                            <AreaChartCustom
                              data={displayedData.sysStatus.slice(
                                startIndex,
                                endIndex
                              )}
                              title={t('pages.maintenance.ingestion.sys_status')}
                              setLoading={setLoading}
                              chartOptions={{
                                type: 'stepAfter',
                                yAxisDomain: [0, 3],
                                tickCount: 4,
                                tickFormatter: (value) =>
                                  securityStatusLabels[value] || String(value),
                              }}
                              highlightedPoints={highlightedPoints}
                            />
                          </Suspense>
                        )}
                        {graphsVisibility.state && (
                          <Suspense fallback={<ProgressSpinner />}>
                            <AreaChartCustom
                              data={displayedData.state.slice(
                                startIndex,
                                endIndex
                              )}
                              title={t('pages.maintenance.ingestion.state')}
                              setLoading={setLoading}
                              highlightedPoints={highlightedPoints}
                              chartOptions={{
                                type: 'stepAfter',
                                yAxisDomain: isAtlas
                                  ? [0, 7]
                                  : isClinic
                                    ? [0, 13]
                                    : [0, 10],
                                tickCount: isAtlas ? 8 : isClinic ? 14 : 11,
                                tickFormatter: (value) => {
                                  return stateLabels[value] || String(value);
                                },
                              }}
                            />
                          </Suspense>
                        )}
                        {graphsVisibility.global_position && (
                          <Suspense fallback={<ProgressSpinner />}>
                            <AreaChartCustom
                              data={displayedData.globalPosition.slice(
                                startIndex,
                                endIndex
                              )}
                              title={t(
                                'pages.maintenance.ingestion.global_position'
                              )}
                              setLoading={setLoading}
                              highlightedPoints={highlightedPoints}
                              chartOptions={{
                                type: 'stepAfter',
                                yAxisDomain: [0, 5],
                                tickCount: 6,
                                tickFormatter: (value) =>
                                  globalPositionLabels[value] || String(value),
                              }}
                            />
                          </Suspense>
                        )}
                      </Card>
                    )}

                  {/* Gráficas del grupo "Batería" */}
                  {(graphsVisibility.battery ||
                    graphsVisibility.battery_voltage ||
                    graphsVisibility.energy_monitor ||
                    graphsVisibility.battery_temp) && (
                      <Card className="w-full p-4 shadow-xl rounded-lg surface-card border-round-xl shadow-3">
                        <div className="text-4xl font-bold mb-4 ">
                          {t('pages.maintenance.ingestion.batery')}
                        </div>
                        {graphsVisibility.battery && (
                          <Suspense fallback={<ProgressSpinner />}>
                            <AreaChartCustom
                              data={displayedData.capacityData.slice(
                                startIndex,
                                endIndex
                              )}
                              title={`${t('pages.maintenance.ingestion.percentage')} (%)`}
                              setLoading={setLoading}
                              highlightedPoints={highlightedPoints}
                              chartOptions={{
                                type: 'monotone',
                                yAxisDomain: [0, 100],
                                tickCount: 6,
                                tickFormatter: (value) => `${value}`,
                              }}
                            />
                          </Suspense>
                        )}
                        {graphsVisibility.battery_voltage && (
                          <Suspense fallback={<ProgressSpinner />}>
                            <AreaChartCustom
                              data={displayedData.voltageData.slice(
                                startIndex,
                                endIndex
                              )}
                              title={`${t('pages.maintenance.ingestion.voltage')} (V)`}
                              setLoading={setLoading}
                              highlightedPoints={highlightedPoints}
                              chartOptions={{
                                type: 'monotone',
                                yAxisDomain: [30, 60],
                                tickCount: 6,
                                tickFormatter: (value) => `${value}`,
                              }}
                            />
                          </Suspense>
                        )}
                        {graphsVisibility.energy_monitor && (
                          <Suspense fallback={<ProgressSpinner />}>
                            <AreaChartCustom
                              data={displayedData.energyMonitor.slice(
                                startIndex,
                                endIndex
                              )}
                              title={`${t('pages.maintenance.ingestion.energy_monitor')} (mA)`}
                              setLoading={setLoading}
                              highlightedPoints={highlightedPoints}
                              chartOptions={{
                                type: 'monotone',
                                yAxisDomain: [-40, 20],
                                tickCount: 6,
                                tickFormatter: (value) => `${value}`,
                              }}
                            />
                          </Suspense>
                        )}
                        {graphsVisibility.battery_temp && (
                          <Suspense fallback={<ProgressSpinner />}>
                            <AreaChartCustom
                              data={displayedData.tempData.slice(
                                startIndex,
                                endIndex
                              )}
                              title={`${t('pages.maintenance.ingestion.temperature')} (°C)`}
                              setLoading={setLoading}
                              highlightedPoints={highlightedPoints}
                              chartOptions={{
                                type: 'monotone',
                                yAxisDomain: [0, 100],
                                tickCount: 6,
                                tickFormatter: (value) => `${value}`,
                              }}
                            />
                          </Suspense>
                        )}
                      </Card>
                    )}

                  {/* Gráficas del grupo "Motores" */}
                  {(graphsVisibility.motorsRef ||
                    graphsVisibility.motorsCurrent ||
                    graphsVisibility.motorsTemp ||
                    graphsVisibility.motorsTorque ||
                    graphsVisibility.motorsInt ||
                    graphsVisibility.tempEpos ||
                    graphsVisibility.assistedTorque ||
                    graphsVisibility.refAndRealTorque) && (
                      <Card className="w-full p-4 shadow-xl rounded-lg surface-card border-round-xl shadow-3">
                        <div className="text-4xl font-bold mb-4 ">
                          {t('pages.maintenance.ingestion.exo')}
                        </div>
                        {graphsVisibility.motorsRef && (
                          <Suspense fallback={<ProgressSpinner />}>
                            <MultiLineChart
                              data={displayedData.motorsAng.slice(
                                startIndex,
                                endIndex
                              )}
                              title={`${t('pages.maintenance.ingestion.motorsRef')} (°)`}
                              setLoading={setLoading}
                              highlightedPoints={highlightedPoints}
                              chartOptions={{
                                yAxisDomain: !isAtlas ? [-15, 50] : [-15, 100],
                                tickCount: 6,
                                tickFormatter: (value) => `${value}`,
                              }}
                            />
                          </Suspense>
                        )}
                        {graphsVisibility.motorsCurrent && (
                          <Suspense fallback={<ProgressSpinner />}>
                            <MultiLineChart
                              data={displayedData.motorVoltageData.slice(
                                startIndex,
                                endIndex
                              )}
                              title={`${t('pages.maintenance.ingestion.motorsCurrent')} (mA)`}
                              setLoading={setLoading}
                              highlightedPoints={highlightedPoints}
                              chartOptions={{
                                yAxisDomain: [0, 150],
                                tickCount: 6,
                                tickFormatter: (value) => `${value}`,
                              }}
                            />
                          </Suspense>
                        )}
                        {graphsVisibility.motorsTemp && (
                          <Suspense fallback={<ProgressSpinner />}>
                            <MultiLineChart
                              data={displayedData.temperatureData.slice(
                                startIndex,
                                endIndex
                              )}
                              title={`${t('pages.maintenance.ingestion.motorsTemp')} (°C)`}
                              setLoading={setLoading}
                              highlightedPoints={highlightedPoints}
                              chartOptions={{
                                yAxisDomain: [-70, 70],
                                tickCount: 6,
                                tickFormatter: (value) => `${value}`,
                              }}
                            />
                          </Suspense>
                        )}
                        {graphsVisibility.motorsTorque && (
                          <Suspense fallback={<ProgressSpinner />}>
                            <MultiLineChart
                              data={displayedData.torqueData.slice(
                                startIndex,
                                endIndex
                              )}
                              highlightedPoints={highlightedPoints}
                              title={`${t('pages.maintenance.ingestion.motorsTorque')} (Nm)`}
                              setLoading={setLoading}
                              chartOptions={{
                                tickCount: 6,
                                tickFormatter: (value) => `${value}`,
                              }}
                            />
                          </Suspense>
                        )}
                        {graphsVisibility.tempEpos && (
                          <Suspense fallback={<ProgressSpinner />}>
                            <MultiLineChart
                              data={displayedData.tempEpos.slice(
                                startIndex,
                                endIndex
                              )}
                              highlightedPoints={highlightedPoints}
                              title={`${t('pages.maintenance.ingestion.tempEpos')} (°C)`}
                              setLoading={setLoading}
                              chartOptions={{
                                tickCount: 6,
                                tickFormatter: (value) => `${value}`,
                              }}
                            />
                          </Suspense>
                        )}
                        {graphsVisibility.motorsInt && (
                          <Suspense fallback={<ProgressSpinner />}>
                            <MultiLineChart
                              data={displayedData.motorsInt.slice(
                                startIndex,
                                endIndex
                              )}
                              highlightedPoints={highlightedPoints}
                              title={`${t('pages.maintenance.ingestion.motorsInt')} (Nm)`}
                              setLoading={setLoading}
                              chartOptions={{
                                yAxisDomain: [0, 10],
                                tickCount: 6,
                                tickFormatter: (value) => `${value}`,
                              }}
                            />
                          </Suspense>
                        )}
                        {graphsVisibility.assistedTorque && (
                          <Suspense fallback={<ProgressSpinner />}>
                            <MultiLineChart
                              data={displayedData.assistedTorque.slice(
                                startIndex,
                                endIndex
                              )}
                              highlightedPoints={highlightedPoints}
                              title={`${t('pages.maintenance.ingestion.assistedTorque')} (Nm)`}
                              setLoading={setLoading}
                              chartOptions={{
                                yAxisDomain: [0, 10],
                                tickCount: 6,
                                tickFormatter: (value) => `${value}`,
                              }}
                            />
                          </Suspense>
                        )}
                        {graphsVisibility.refAndRealTorque && (
                          <Suspense fallback={<ProgressSpinner />}>
                            <MultiLineChart
                              data={displayedData.refAndRealTorque.slice(
                                startIndex,
                                endIndex
                              )}
                              highlightedPoints={highlightedPoints}
                              title={`${t('pages.maintenance.ingestion.refAndRealTorque')} (Nm)`}
                              setLoading={setLoading}
                              chartOptions={{
                                yAxisDomain: [0, 10],
                                tickCount: 6,
                                tickFormatter: (value) => `${value}`,
                              }}
                            />
                          </Suspense>
                        )}
                      </Card>
                    )}

                  {/* Gráficas del grupo "Sensores y Control" */}
                  {(graphsVisibility.elev_sens ||
                    graphsVisibility.elev_ctrl ||
                    graphsVisibility.sit_angle ||
                    graphsVisibility.env_temp ||
                    graphsVisibility.load_cell ||
                    graphsVisibility.imuData) && (
                      <Card className="w-full p-4 shadow-xl rounded-lg surface-card border-round-xl shadow-3">
                        <div className="text-4xl font-bold mb-4 ">
                          {t('pages.maintenance.ingestion.walker_info')}
                        </div>
                        {graphsVisibility.elev_sens && (
                          <Suspense fallback={<ProgressSpinner />}>
                            <AreaChartCustom
                              data={displayedData.elevSens.slice(
                                startIndex,
                                endIndex
                              )}
                              title={`${t('pages.maintenance.ingestion.elev_sens')} (mm)`}
                              setLoading={setLoading}
                              highlightedPoints={highlightedPoints}
                              chartOptions={{
                                tickFormatter: (value) => `${value}`,
                              }}
                            />
                          </Suspense>
                        )}
                        {graphsVisibility.elev_ctrl && (
                          <Suspense fallback={<ProgressSpinner />}>
                            <AreaChartCustom
                              data={displayedData.elevCtrl.slice(
                                startIndex,
                                endIndex
                              )}
                              title={`${t('pages.maintenance.ingestion.elev_ctrl')} (inc)`}
                              setLoading={setLoading}
                              highlightedPoints={highlightedPoints}
                              chartOptions={{
                                tickFormatter: (value) => `${value}`,
                              }}
                            />
                          </Suspense>
                        )}
                        {graphsVisibility.sit_angle && (
                          <Suspense fallback={<ProgressSpinner />}>
                            <AreaChartCustom
                              data={displayedData.sitAngle.slice(
                                startIndex,
                                endIndex
                              )}
                              title={`${t('pages.maintenance.ingestion.sit_angle')} (°)`}
                              setLoading={setLoading}
                              highlightedPoints={highlightedPoints}
                              chartOptions={{
                                tickFormatter: (value) => `${value}`,
                              }}
                            />
                          </Suspense>
                        )}
                        {graphsVisibility.load_cell && (
                          <Suspense fallback={<ProgressSpinner />}>
                            <AreaChartCustom
                              data={displayedData.loadCell.slice(
                                startIndex,
                                endIndex
                              )}
                              title={`${t('pages.maintenance.ingestion.loadCell')} (inc)`}
                              setLoading={setLoading}
                              highlightedPoints={highlightedPoints}
                              chartOptions={{
                                tickFormatter: (value) => `${value}`,
                              }}
                            />
                          </Suspense>
                        )}
                        {graphsVisibility.env_temp && (
                          <Suspense fallback={<ProgressSpinner />}>
                            <AreaChartCustom
                              data={displayedData.envTemp.slice(
                                startIndex,
                                endIndex
                              )}
                              title={`${t('pages.maintenance.ingestion.env_temp')} (°C)`}
                              setLoading={setLoading}
                              highlightedPoints={highlightedPoints}
                              chartOptions={{
                                tickFormatter: (value) => `${value}`,
                              }}
                            />
                          </Suspense>
                        )}
                        {graphsVisibility.imuData && (
                          <Suspense fallback={<ProgressSpinner />}>
                            <AreaChartCustom
                              data={displayedData.imuData.slice(
                                startIndex,
                                endIndex
                              )}
                              title={`${t('pages.maintenance.ingestion.imuData')} (°)`}
                              setLoading={setLoading}
                              highlightedPoints={highlightedPoints}
                              chartOptions={{
                                tickFormatter: (value) => `${value}`,
                              }}
                            />
                          </Suspense>
                        )}
                      </Card>
                    )}
                  {showScrollTopButton && (
                    <Button
                      icon="pi pi-arrow-up"
                      className="p-button-rounded p-button-primary scroll-to-top-btn"
                      onClick={scrollToButton}
                      style={{
                        position: 'fixed',
                        bottom: '20px',
                        right: '20px',
                        zIndex: 1000,
                      }}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Graphs;
